import React, { useRef, useState, useEffect } from 'react';
import service from '../../services/breadorders.service';
import PrintContainer from '../PrintContainer';
import ThermalReceipt, { ThermalReceiptRow, ThermalReceiptSection } from '../receipts/ThermalReceipt';
import storeLogo from '../../images/logo-los-portales.png';
import { editBread } from '../../helpers/breads';
import { formatPhoneForDisplay } from '../../helpers/phone';
import { getOrderAuditLog } from '../../services/orderAudit.service';
import OrderAuditTimeline from '../shared/OrderAuditTimeline';
import styles from './Bread.module.css';

export default function BreadOrder({ bread }) {
    const contentRef = useRef();
    const [isEditing, setIsEditing] = useState(false);
    const [editedBread, setEditedBread] = useState(bread);
    const [auditLog, setAuditLog] = useState([]);
    const [auditVersion, setAuditVersion] = useState(0);
    const breadItems = Array.isArray(bread.bread) ? bread.bread : [];

    useEffect(() => {
        getOrderAuditLog('breads', bread.id).then(setAuditLog).catch(console.error);
    }, [bread.id, auditVersion]);

    function deleteBreadOrder() {
        if (window.confirm('Estas seguro que quieres borrar esta orden?')) {
            service.deleteBreadOrder(bread.id);
        }
    }

    function handleInputChange(e) {
        const { name, value } = e.target;
        setEditedBread(prevBread => ({ ...prevBread, [name]: value }));
    }

    function handleBreadChange(index, field, value) {
        const newBreads = [...editedBread.bread];
        newBreads[index][field] = value;
        setEditedBread(prevBread => ({ ...prevBread, bread: newBreads }));
    }

    function addBread() {
        setEditedBread(prevBread => ({
            ...prevBread,
            bread: [...prevBread.bread, { bread: '', quantity: '' }]
        }));
    }

    function removeBreadItem(index) {
        setEditedBread(prevBread => ({
            ...prevBread,
            bread: prevBread.bread.filter((_, i) => i !== index)
        }));
    }

    async function saveChanges() {
        try {
            console.log('Saving changes:', editedBread);
            await editBread(editedBread);
            setIsEditing(false);
            setAuditVersion(v => v + 1);
        } catch (error) {
            console.error('Error updating bread order:', error);
            alert('Error updating bread order. Please try again.');
        }
    }

    const renderInput = (key, value) => {
        switch (key) {
            case 'date':
                return <input type="date" name={key} value={value} onChange={handleInputChange} />;
            case 'time':
                return (
                    <select name={key} value={value} onChange={handleInputChange}>
                        <option value="11am">11:00 AM</option>
                        <option value="12pm">12:00 PM</option>
                        <option value="1pm">1:00 PM</option>
                        <option value="2pm">2:00 PM</option>
                        <option value="3pm">3:00 PM</option>
                        <option value="4pm">4:00 PM</option>
                        <option value="5pm">5:00 PM</option>
                        <option value="6pm">6:00 PM</option>
                        <option value="7pm">7:00 PM</option>
                        <option value="8pm">8:00 PM</option>
                    </select>
                );
            case 'phoneNumber':
                return <input type="tel" name={key} value={value} onChange={handleInputChange} />;
            case 'comments':
                return <textarea name={key} value={value} onChange={handleInputChange} />;
            case 'bread':
                return value.map((item, index) => (
                    <div key={index} className="formEntry">
                        <input placeholder='cantidad' type="number" value={item.quantity} name={`quantity-${index}`} onChange={(e) => handleBreadChange(index, 'quantity', e.target.value)} />
                        <input placeholder='nombre de pan' type="text" value={item.bread} name={`bread-${index}`} onChange={(e) => handleBreadChange(index, 'bread', e.target.value)} />
                        <button type="button" onClick={() => removeBreadItem(index)}>Eliminar</button>
                    </div>
                ));
            default:
                return <input type="text" name={key} value={value} onChange={handleInputChange} />;
        }
    };

    const receiptData = {
        title: 'Pan Especial',
        subtitle: `${bread.firstName} ${bread.lastName}`,
        meta: [
            { label: 'Dia', value: bread.date },
            { label: 'Hora', value: bread.time },
        ],
        sections: [
            {
                title: 'Contacto',
                rows: [
                    { label: 'Telefono', value: formatPhoneForDisplay(bread.phoneNumber) },
                ],
            },
            {
                title: 'Pan',
                items: breadItems.map(item => ({
                    text: `${item.quantity}x ${item.bread}`,
                })),
            },
            {
                title: 'Comentarios',
                rows: [
                    { label: 'Notas', value: bread.comments },
                    { label: 'Cajer@', value: bread.cashier },
                ],
            },
        ],
        footerNote: 'Verificar telefonos antes de llamar',
    };

    return (
        <div className="">
            <PrintContainer className="printContainer" contentRef={contentRef} receiptData={receiptData}>
                <ThermalReceipt
                    logoSrc={storeLogo}
                    logoAlt="Los Portales"
                    title="Pan Especial"
                    subtitle={`${bread.firstName} ${bread.lastName}`}
                    meta={[
                        { label: 'Dia', value: isEditing ? renderInput('date', editedBread.date) : bread.date },
                        { label: 'Hora', value: isEditing ? renderInput('time', editedBread.time) : bread.time }
                    ]}
                    footerNote="Verificar telefonos antes de llamar"
                >
                    <ThermalReceiptSection title="Contacto">
                        <ThermalReceiptRow
                            label="Telefono"
                            value={isEditing ? renderInput('phoneNumber', editedBread.phoneNumber) : formatPhoneForDisplay(bread.phoneNumber)}
                        />
                    </ThermalReceiptSection>
                    <ThermalReceiptSection title="Pan">
                        {isEditing ? (
                            <>
                                {renderInput('bread', editedBread.bread)}
                                <button type="button" onClick={addBread}>Agregar Pan</button>
                            </>
                        ) : breadItems.length > 0 ? (
                            <ul className="thermal-receipt__list">
                                {breadItems.map((item, index) => (
                                    <li className="thermal-receipt__list-item" key={`bread-${bread.id}-${index}`}>
                                        <span>{item.quantity}x {item.bread}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="thermal-receipt__empty">Sin panes registrados</p>
                        )}
                    </ThermalReceiptSection>
                    <ThermalReceiptSection title="Comentarios">
                        <ThermalReceiptRow
                            label="Notas"
                            value={isEditing ? renderInput('comments', editedBread.comments) : bread.comments}
                        />
                        <ThermalReceiptRow
                            label="Cajer@"
                            value={isEditing ? renderInput('cashier', editedBread.cashier) : bread.cashier}
                        />
                    </ThermalReceiptSection>
                </ThermalReceipt>
            </PrintContainer>
            <div>
                {isEditing ? (
                    <button onClick={saveChanges}>Guardar</button>
                ) : (
                    <button onClick={() => setIsEditing(true)}>Editar</button>
                )}
                <button onClick={deleteBreadOrder} style={{ backgroundColor: 'red' }}>BORRAR</button>
            </div>
            <OrderAuditTimeline auditLog={auditLog} />
        </div>
    );
}
